import { Fragment, useState } from "react";
import { useQuery } from "../api/useQuery";
import { query } from "../api/client";
import { ShiftPatrols } from "../components/ShiftPatrols";
import { ShiftEquipment } from "../components/ShiftEquipment";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { SupervisorNotes } from "../components/SupervisorNotes";
import { fmtDateTime, fmtDuration } from "../components/format";
import type { Page, Shift, Territory, User } from "../api/types";

const PAGE_SIZE = 25;

export function ShiftsPage() {
  const { me } = useAuth();
  const [territory, setTerritory] = useState("");
  const [inspector, setInspector] = useState("");
  const territories = useQuery<{ items: Territory[] }>("/api/v1/territories");
  // Список сотрудников доступен руководителям; инспектору фильтр не нужен.
  const users = useQuery<{ items: User[] }>(me?.can_manage_users ? "/api/v1/users" : null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const path =
    "/api/v1/shifts" +
    query({
      from: from ? new Date(from).toISOString() : undefined,
      // Верхняя граница включает выбранный день целиком.
      to: to ? new Date(new Date(to).getTime() + 86400000).toISOString() : undefined,
      territory_id: territory,
      inspector_id: inspector,
      limit: PAGE_SIZE,
      offset,
    });

  const { data, error, loading, reload } = useQuery<Page<Shift>>(path);

  return (
    <>
      <div className="page-head">
        <h2>Смены и обходы</h2>
        {data && <p className="muted small" style={{ margin: 0 }}>всего: {data.total}</p>}
      </div>

      <div className="filters">
        <label className="field">
          С даты
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} />
        </label>
        <label className="field">
          По дату
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} />
        </label>
        {(territories.data?.items.length ?? 0) > 1 && (
          <label className="field">
            Территория
            <select value={territory} onChange={(e) => { setTerritory(e.target.value); setOffset(0); }}>
              <option value="">Все</option>
              {territories.data!.items.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}
        {users.data && (
          <label className="field">
            Инспектор
            <select value={inspector} onChange={(e) => { setInspector(e.target.value); setOffset(0); }}>
              <option value="">Все</option>
              {users.data.items.filter((u) => u.role === "inspector").map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </label>
        )}
        {(from || to || territory || inspector) && (
          <button className="secondary" onClick={() => { setFrom(""); setTo(""); setTerritory(""); setInspector(""); setOffset(0); }}>
            Сбросить
          </button>
        )}
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {loading && !data && <p className="notice">Загрузка…</p>}

      {data && data.items.length === 0 && !loading && (
        <p className="notice">За выбранный период смен нет.</p>
      )}

      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Инспектор</th>
                  <th>Территория</th>
                  <th>Начало</th>
                  <th>Длительность</th>
                  <th className="num">Обходы</th>
                  <th className="num">Происшествия</th>
                  <th className="num">Наблюдения</th>
                  <th>Замечания</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <Fragment key={s.id}>
                  <tr>
                    <td>{s.inspector_name}</td>
                    <td>{s.territory_name}</td>
                    <td>{fmtDateTime(s.started_at)}</td>
                    <td>
                      {s.status === "open"
                        ? <span className="badge warn">смена открыта</span>
                        : fmtDuration(s.started_at, s.ended_at)}
                    </td>
                    <td className="num">{s.patrols}</td>
                    <td className="num">{s.incidents}</td>
                    <td className="num">{s.observations}</td>
                    <td>
                      {s.pending_files > 0 && (
                        <span className="badge warn" title="Метаданные фото получены, содержимое ещё догружается">
                          фото в очереди: {s.pending_files}
                        </span>
                      )}{" "}
                      {s.equipment_issues > 0 && (
                        <span className="badge danger">расхождения по имуществу: {s.equipment_issues}</span>
                      )}{" "}
                      {s.needs_review > 0 && (
                        <span className="badge warn">требует проверки: {s.needs_review}</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="secondary"
                        aria-expanded={expanded === s.id}
                        onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                      >
                        {expanded === s.id ? "Свернуть" : "Подробнее"}
                      </button>
                    </td>
                  </tr>
                  {expanded === s.id && (
                    <tr>
                      <td colSpan={9} style={{ background: "var(--bg)" }}>
                        <div className="filters" style={{ margin: "0 0 8px" }}>
                          <button className="secondary"
                            onClick={() => void api.download(`/api/v1/shifts/${s.id}/report`, "shift.pdf").catch(() => alert("Не удалось сформировать отчёт"))}>
                            Отчёт о смене, PDF
                          </button>
                        </div>
                        <div className="section-title">Обходы</div>
                        <ShiftPatrols shiftId={s.id} />
                        <div className="section-title" style={{ marginTop: 16 }}>Имущество: выдача и сдача</div>
                        <ShiftEquipment shiftId={s.id} onChanged={reload} />
                        <div className="section-title" style={{ marginTop: 16 }}>Заметки начальника</div>
                        <SupervisorNotes path={`/api/v1/shifts/${s.id}/notes`} canWrite={me?.role !== "inspector"} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="filters" style={{ marginTop: 14 }}>
          <button className="secondary" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Назад
          </button>
          <span className="muted small">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} из {data.total}
          </span>
          <button className="secondary" disabled={offset + PAGE_SIZE >= data.total}
            onClick={() => setOffset(offset + PAGE_SIZE)}>
            Далее
          </button>
        </div>
      )}
    </>
  );
}
